<template>
  <section class="card-use-instruction-page">
    <AppNavBar :title="t('card.useInstruction')" background-color="#ffffff" />

    <main
      v-if="secondCheckGuideVisible"
      class="card-use-instruction-page__body"
    >
      <div class="platform-tabs">
        <button
          v-for="(item, index) in secondCheckPlatformList"
          :key="item.name"
          type="button"
          :class="{ active: secondCheckPlatformIndex === index }"
          @click="selectSecondCheckPlatform(index)"
        >
          {{ item.name }}
        </button>
      </div>

      <DesktopSwipe
        ref="secondCheckPlatformSwipe"
        class="instruction-swipe"
        :loop="false"
        :show-indicators="false"
        @change="secondCheckPlatformIndex = $event"
      >
        <van-swipe-item
          v-for="item in secondCheckPlatformList"
          :key="item.name"
        >
          <article class="instruction-panel">
            <header class="platform-hero">
              <div class="platform-hero__content">
                <h2>
                  {{ t("card.useInstructionText7")
                  }}<span>{{ item.name }}</span>
                </h2>
                <p>{{ item.description }}</p>
              </div>
              <app-image class="platform-hero__image" :src="item.hero" alt="" />
            </header>

            <section class="bind-card">
              <div
                v-for="step in item.steps"
                :key="step.step"
                class="bind-step"
              >
                <div class="bind-step__index">
                  <div class="bind-step__index-head">
                    <span>{{ step.step }}</span>
                  </div>
                  <i v-if="!step.last" />
                </div>
                <div class="bind-step__content">
                  <app-image :src="step.icon" alt="" />
                  <div class="bind-step__text">
                    <h3>{{ step.title }}</h3>
                    <p>{{ step.desc }}</p>
                  </div>
                </div>
              </div>
            </section>
          </article>
        </van-swipe-item>
      </DesktopSwipe>
    </main>

    <main v-else class="card-use-instruction-page__body">
      <div class="platform-tabs">
        <button
          v-for="(item, index) in platformList"
          :key="item.name"
          type="button"
          :class="{ active: activePlatformIndex === index }"
          @click="selectPlatform(index)"
        >
          {{ item.name }}
        </button>
      </div>

      <DesktopSwipe
        ref="platformSwipe"
        class="instruction-swipe"
        :loop="false"
        :show-indicators="false"
        @change="activePlatformIndex = $event"
      >
        <van-swipe-item v-for="item in platformList" :key="item.name">
          <article class="instruction-panel">
            <header class="platform-hero">
              <div class="platform-hero__content">
                <h2>
                  {{ t("card.useInstructionText7")
                  }}<span>{{ item.name }}</span>
                </h2>
                <p>{{ item.description }}</p>
              </div>
              <app-image
                class="platform-hero__image"
                :src="item.image"
                alt=""
              />
            </header>

            <section class="bind-card">
              <div
                v-for="step in item.steps"
                :key="step.step"
                class="bind-step"
              >
                <div class="bind-step__index">
                  <div class="bind-step__index-head">
                    <span>{{ step.step }}</span>
                  </div>
                  <i v-if="!step.last" />
                </div>
                <div class="bind-step__content">
                  <app-image :src="step.icon" alt="" />
                  <div class="bind-step__text">
                    <h3>{{ step.title }}</h3>
                    <p>{{ step.desc }}</p>
                  </div>
                </div>
              </div>
            </section>
          </article>
        </van-swipe-item>
      </DesktopSwipe>
    </main>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import DesktopSwipe from "@/components/DesktopSwipe.vue";
import { cardMyList } from "@/api/card";
import { useCardStore } from "@/stores/card";
import { useProfileStore } from "@/stores/profile";
import { getDeviceId } from "@/utils/device";
import { getRouteState } from "@/utils/route-state";
import alipayImage from "@/assets/images/cardImage/bindCard_aliPay.png";
import googlePayImage from "@/assets/images/cardImage/bindCard_googlePay.png";
import applePayImage from "@/assets/images/cardImage/bindCard_applePay.png";
import useIconOne from "@/assets/images/cardImage/use_icon_1.png";
import useIconTwo from "@/assets/images/cardImage/use_icon_2.png";
import useIconThree from "@/assets/images/cardImage/use_icon_3.png";
import useIconFour from "@/assets/images/cardImage/use_icon_4.png";
import useIconFive from "@/assets/images/cardImage/use_icon_5.png";
import useIconSix from "@/assets/images/cardImage/use_icon_6.png";
import useIconSeven from "@/assets/images/cardImage/use_icon_7.png";
import useIconEight from "@/assets/images/cardImage/use_icon_8.png";
import useIconNine from "@/assets/images/cardImage/use_icon_9.png";
import useIconTen from "@/assets/images/cardImage/use_icon_10.png";
import secondCheckPaypalHero from "@/assets/images/cardImage/use-instruction-paypal.png";
import secondCheckWechatHero from "@/assets/images/cardImage/use-instruction-wechat-pay.png";

const { t } = useI18n();
const route = useRoute();
const cardStore = useCardStore();
const profileStore = useProfileStore();
const platformSwipe = ref(null);
const activePlatformIndex = ref(0);
const secondCheckPlatformSwipe = ref(null);
const secondCheckPlatformIndex = ref(0);
const secondCheckGuideVisible = ref(false);
const pageState = computed(() =>
  getRouteState(route, "card-use-instruction", {}),
);

const cardType = computed(() => Number(pageState.value?.cardType || 2));

function isTrue(value) {
  return (
    value === true ||
    value === 1 ||
    ["true", "1"].includes(String(value).trim().toLowerCase())
  );
}

function getCardList(result) {
  return Array.isArray(result?.list)
    ? result.list
    : Array.isArray(result)
      ? result
      : [];
}

async function loadSecondCheckState() {
  const address = profileStore.platformAddress;
  if (!address) {
    secondCheckGuideVisible.value = false;
    return;
  }

  try {
    const result = await cardMyList(
      {
        address,
        pageNum: 1,
        pageSize: 999,
        deviceToken: getDeviceId(),
        cardType: cardType.value,
      },
      { meta: { showLoading: false } },
    );
    const list = getCardList(result);
    const currentCardId = String(
      pageState.value?.cardId ||
        cardStore.currentCardByType(cardType.value)?.id ||
        "",
    ).trim();
    const currentCard = currentCardId
      ? list.find((item) => String(item?.id || "") === currentCardId)
      : list[0];
    secondCheckGuideVisible.value = isTrue(currentCard?.secondCheck);
  } catch (error) {
    secondCheckGuideVisible.value = false;
  }
}

function createStep(step, icon, title, desc, last = false) {
  return { step, icon, title, desc, last };
}

const platformList = computed(() => [
  {
    name: "AliPay",
    image: alipayImage,
    description: t("card.bindCardInfo1"),
    steps: [
      createStep(
        "1",
        useIconOne,
        t("card.useInstructionText10"),
        t("card.useInstructionText11"),
      ),
      createStep(
        "2",
        useIconTwo,
        t("card.useInstructionText12"),
        t("card.useInstructionText13"),
      ),
      createStep(
        "3",
        useIconThree,
        t("card.useInstructionText14"),
        t("card.useInstructionText15"),
      ),
      createStep(
        "4",
        useIconFour,
        t("card.useInstructionText16"),
        t("card.useInstructionText17"),
      ),
      createStep(
        "5",
        useIconFive,
        t("card.useInstructionText18"),
        t("card.useInstructionText19"),
        true,
      ),
    ],
  },
  {
    name: "Google Pay",
    image: googlePayImage,
    description: t("card.bindCardInfo2"),
    steps: [
      createStep(
        "1",
        useIconEight,
        t("card.bindCardInfo4"),
        t("card.bindCardInfo5"),
      ),
      createStep(
        "2",
        useIconTwo,
        t("card.bindCardInfo6"),
        t("card.bindCardInfo7"),
      ),
      createStep(
        "3",
        useIconThree,
        t("card.bindCardInfo8"),
        t("card.bindCardInfo9"),
      ),
      createStep(
        "4",
        useIconFour,
        t("card.bindCardInfo10"),
        t("card.bindCardInfo11"),
      ),
      createStep(
        "5",
        useIconFive,
        t("card.useInstructionText18"),
        t("card.bindCardInfo12"),
        true,
      ),
    ],
  },
  {
    name: "Apple Pay",
    image: applePayImage,
    description: t("card.bindCardInfo3"),
    steps: [
      createStep(
        "1",
        useIconSeven,
        t("card.bindCardInfo13"),
        t("card.bindCardInfo14"),
      ),
      createStep(
        "2",
        useIconTwo,
        t("card.bindCardInfo6"),
        t("card.bindCardInfo7"),
      ),
      createStep(
        "3",
        useIconSix,
        t("card.bindCardInfo15"),
        t("card.bindCardInfo16"),
      ),
      createStep(
        "4",
        useIconFour,
        t("card.bindCardInfo17"),
        t("card.bindCardInfo18"),
      ),
      createStep(
        "5",
        useIconFive,
        t("card.useInstructionText18"),
        t("card.bindCardInfo12"),
        true,
      ),
    ],
  },
]);

function createSecondCheckStep(step, icon, title, desc, last = false) {
  return { step, icon, title, desc, last };
}

const secondCheckPlatformList = computed(() => [
  {
    name: t("card.secondCheckGuide.paypal.name"),
    hero: secondCheckPaypalHero,
    description: t("card.secondCheckGuide.paypal.description"),
    steps: [
      createSecondCheckStep(
        "1",
        useIconNine,
        t("card.secondCheckGuide.paypal.step1Title"),
        t("card.secondCheckGuide.paypal.step1Desc"),
      ),
      createSecondCheckStep(
        "2",
        useIconTwo,
        t("card.secondCheckGuide.paypal.step2Title"),
        t("card.secondCheckGuide.paypal.step2Desc"),
      ),
      createSecondCheckStep(
        "3",
        useIconThree,
        t("card.secondCheckGuide.paypal.step3Title"),
        t("card.secondCheckGuide.paypal.step3Desc"),
      ),
      createSecondCheckStep(
        "4",
        useIconFour,
        t("card.secondCheckGuide.paypal.step4Title"),
        t("card.secondCheckGuide.paypal.step4Desc"),
      ),
      createSecondCheckStep(
        "5",
        useIconFive,
        t("card.secondCheckGuide.paypal.step5Title"),
        t("card.secondCheckGuide.paypal.step5Desc"),
        true,
      ),
    ],
  },
  {
    name: t("card.secondCheckGuide.googlePay.name"),
    hero: googlePayImage,
    description: t("card.bindCardInfo2"),
    steps: [
      createSecondCheckStep(
        "1",
        useIconEight,
        t("card.bindCardInfo4"),
        t("card.bindCardInfo5"),
      ),
      createSecondCheckStep(
        "2",
        useIconTwo,
        t("card.bindCardInfo6"),
        t("card.bindCardInfo7"),
      ),
      createSecondCheckStep(
        "3",
        useIconThree,
        t("card.bindCardInfo8"),
        t("card.bindCardInfo9"),
      ),
      createSecondCheckStep(
        "4",
        useIconFour,
        t("card.bindCardInfo10"),
        t("card.bindCardInfo11"),
      ),
      createSecondCheckStep(
        "5",
        useIconFive,
        t("card.useInstructionText18"),
        t("card.bindCardInfo12"),
        true,
      ),
    ],
  },
  {
    name: t("card.secondCheckGuide.wechatPay.name"),
    hero: secondCheckWechatHero,
    description: t("card.secondCheckGuide.wechatPay.description"),
    steps: [
      createSecondCheckStep(
        "1",
        useIconTen,
        t("card.secondCheckGuide.wechatPay.step1Title"),
        t("card.secondCheckGuide.wechatPay.step1Desc"),
      ),
      createSecondCheckStep(
        "2",
        useIconTwo,
        t("card.secondCheckGuide.wechatPay.step2Title"),
        t("card.secondCheckGuide.wechatPay.step2Desc"),
      ),
      createSecondCheckStep(
        "3",
        useIconThree,
        t("card.secondCheckGuide.wechatPay.step3Title"),
        t("card.secondCheckGuide.wechatPay.step3Desc"),
      ),
      createSecondCheckStep(
        "4",
        useIconFour,
        t("card.secondCheckGuide.wechatPay.step4Title"),
        t("card.secondCheckGuide.wechatPay.step4Desc"),
      ),
      createSecondCheckStep(
        "5",
        useIconFive,
        t("card.secondCheckGuide.wechatPay.step5Title"),
        t("card.secondCheckGuide.wechatPay.step5Desc"),
        true,
      ),
    ],
  },
]);

function selectPlatform(index) {
  activePlatformIndex.value = index;
  platformSwipe.value?.swipeTo(index);
}

function selectSecondCheckPlatform(index) {
  secondCheckPlatformIndex.value = index;
  secondCheckPlatformSwipe.value?.swipeTo(index);
}

onMounted(loadSecondCheckState);
</script>

<style scoped lang="scss">
.card-use-instruction-page {
  min-height: var(--app-viewport-height);
  background: #ffffff;
}

.card-use-instruction-page__body {
  padding: 10px 14px calc(20px + env(safe-area-inset-bottom));
}

.platform-tabs {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  padding: 3px;
  border-radius: 30px;
  font-weight: 500;
  background: #eaeaea;

  button {
    min-width: 0;
    padding: 6px 2px;
    border: 0;
    border-radius: 30px;
    background: transparent;
    color: #000000;
    font-size: 16px;
    line-height: 22px;
    white-space: nowrap;
  }

  button.active {
    background: #ffffff;
  }
}

.instruction-swipe {
  margin-top: 20px;

  :deep(.van-swipe-item) {
    box-sizing: border-box;
  }
}

.instruction-panel {
  overflow: hidden;
  border: 1px solid #e2e7f3;
  border-radius: 16px;
  background: #edf5fe;
  margin-top: 20px;
}

.platform-hero {
  display: flex;
  align-items: center;
  min-height: 103px;
  padding: 15px 0px 0px 16px;
}

.platform-hero__content {
  flex: 1;
  min-width: 0;

  h2 {
    margin: 0;
    color: #1e2633;
    font-size: 16px;
    font-weight: 600;
    line-height: 23px;

    span {
      color: var(--wp-color-theme);
    }
  }

  p {
    margin: 6px 0 0;
    color: #51586a;
    font-size: 12px;
    line-height: 18px;
  }
}

.platform-hero__image {
  flex: 0 0 auto;
  width: 145px;
  height: 103px;

  :deep(.van-image__img) {
    object-fit: contain;
  }
}

.bind-card {
  margin: 0px;
  padding: 16px;
  border-radius: 16px;
  background: #ffffff;
}

.bind-step {
  display: flex;
  align-items: stretch;
}

.bind-step__index {
  position: relative;
  flex: 0 0 24px;
}

.bind-step__index-head {
  display: flex;
  height: 48px;
  align-items: center;
  justify-content: center;

  span {
    position: relative;
    z-index: 1;
    display: inline-flex;
    width: 16px;
    height: 16px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: var(--wp-color-theme);
    color: #ffffff;
    font-size: 12px;
    line-height: 16px;
  }
}

.bind-step__index i {
  position: absolute;
  top: 37px;
  bottom: -11px;
  left: 11px;
  width: 2px;
  background-image: linear-gradient(
    to bottom,
    var(--wp-color-theme) 0 4px,
    transparent 4px 8px
  );
  background-size: 2px 8px;
}

.bind-step__content {
  display: flex;
  flex: 1;
  min-width: 0;
  gap: 16px;
  margin-left: 20px;
  padding-bottom: 20px;

  > .app-image {
    flex: 0 0 auto;
    width: 38px;
    height: 38px;
  }
}

.bind-step:last-child .bind-step__content {
  padding-bottom: 0;
}

.bind-step__text {
  flex: 1;
  min-width: 0;

  h3 {
    margin: 0;
    color: #000000;
    font-size: 14px;
    font-weight: 600;
    line-height: 23px;
  }

  p {
    margin: 4px 0 0;
    color: #666666;
    font-size: 12px;
  }
}
</style>
